import * as React from "react"
import { navigate } from "gatsby"
import { Dialog, Box, Text, chakra } from "@chakra-ui/react"
import { useGetStarted } from "../context/GetStartedContext"
import { useLeadForm } from "../context/LeadFormContext"
import { trackEvent } from "../utils/trackEvent"

/**
 * The chooser behind the "Enable Your Portfolio" CTA (GATSBY_SIGNUP_CTA_MODE
 * "choice"): Sign Up runs the self-serve /signup flow; Learn More keeps the
 * existing lead form for anyone who'd rather talk to us first.
 */
const optionStyles = {
  display: "block",
  w: "100%",
  textAlign: "left" as const,
  padding: "18px 20px",
  borderRadius: "12px",
  border: "1px solid #E2E6F2",
  bg: "#FAFBFE",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
  _hover: {
    borderColor: "#6791CB",
    boxShadow: "0 6px 24px rgba(103,145,203,0.18)",
    transform: "translateY(-1px)",
  },
}

const GetStartedChoiceModal: React.FC = () => {
  const { isChoiceOpen, closeChoice } = useGetStarted()
  const leadForm = useLeadForm()

  const chooseSignup = () => {
    trackEvent("cta_choice", { form_id: "get_started_choice", choice: "sign_up" })
    closeChoice()
    void navigate("/signup")
  }

  const chooseLearnMore = () => {
    trackEvent("cta_choice", { form_id: "get_started_choice", choice: "learn_more" })
    closeChoice()
    leadForm.open("get_started_choice")
  }

  return (
    <Dialog.Root
      open={isChoiceOpen}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) closeChoice()
      }}
      placement="center"
      closeOnInteractOutside={true}
    >
      <Dialog.Backdrop bg="rgba(15,22,41,0.55)" backdropFilter="blur(6px)" />
      <Dialog.Positioner>
        <Dialog.Content
          bg="#FFFFFF"
          maxW="440px"
          w="100%"
          p={{ base: "32px 24px", sm: "40px 36px" }}
          borderRadius="16px"
          boxShadow="0 32px 80px rgba(15,22,41,0.18)"
          position="relative"
        >
          <Dialog.CloseTrigger
            position="absolute"
            top="18px"
            right="18px"
            w="32px"
            h="32px"
            borderRadius="50%"
            bg="#F3F5FB"
            border="1px solid #E2E6F2"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            fontSize="1rem"
            color="#8B95B0"
            transition="background 0.2s, color 0.2s"
            _hover={{ bg: "#E2E6F2", color: "#1E2740" }}
            aria-label="Close"
          >
            {"✕"}
          </Dialog.CloseTrigger>

          <Dialog.Title
            fontSize="1.4rem"
            fontWeight={800}
            color="#1E2740"
            letterSpacing="-0.5px"
            mb="6px"
          >
            How would you like to start?
          </Dialog.Title>
          <Dialog.Description fontSize="0.9rem" color="#8B95B0" mb="24px" display="block">
            Both take just a couple of minutes.
          </Dialog.Description>

          <Box display="flex" flexDirection="column" gap="12px">
            <chakra.button type="button" onClick={chooseSignup} {...optionStyles}>
              <Text fontSize="1rem" fontWeight={700} color="#1E2740" mb="2px">
                Sign Up
              </Text>
              <Text fontSize="0.82rem" color="#4A5578">
                Connect your AppFolio, pick your properties, and get set up with
                Spot online.
              </Text>
            </chakra.button>
            <chakra.button type="button" onClick={chooseLearnMore} {...optionStyles}>
              <Text fontSize="1rem" fontWeight={700} color="#1E2740" mb="2px">
                Learn More
              </Text>
              <Text fontSize="0.82rem" color="#4A5578">
                Tell us about your business and our team will reach out within 24
                hours.
              </Text>
            </chakra.button>
          </Box>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  )
}

export default GetStartedChoiceModal
